import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Minus, Maximize2, GripHorizontal, ShoppingCart, Package, User, MapPin, CreditCard, Search, Plus, Trash2, Loader2, ChevronDown, Image as ImageIcon, Tag, Truck, Link2, Copy, Check, BadgePercent, CheckCircle, AlertCircle, RefreshCw, Send, IndianRupee } from 'lucide-react';
import Select from 'react-select';
import { apiRequest } from '@/utils/api-request';
import { createOrder as createRazorpayOrder, sendPaymentLinkWhatsApp, refreshOrderStatus, PaymentLink } from '@/services/razorpayService';

interface CrmTag {
    id: number;
    uuid: string;
    tag_name: string;
    tag_description?: string;
    is_active?: boolean;
}

interface TagOption {
    value: string;
    label: string;
}

type PaymentType = 'cod' | 'checkout_url' | 'razorpay_link';

interface Lead {
    uuid: string;
    name?: string | null;
    email?: string | null;
    phone?: string;
    shopify_customer_id?: string;
    address_1?: string;
    address_2?: string;
    city?: string;
    province?: string;
    zip?: string;
    country?: string;
}

interface ProductVariant {
    id: string;
    title: string;
    price: string;
    inventory_quantity: number;
    sku?: string;
    image_url?: string;
}

interface Product {
    id: string;
    title: string;
    vendor: string;
    product_type: string;
    status: string;
    variants: ProductVariant[];
    image?: {
        src: string;
    };
}

interface SelectedProduct {
    product: Product;
    variant: ProductVariant;
    quantity: number;
}

interface ShopifyAddress {
    address1?: string;
    address2?: string;
    city?: string;
    province?: string;
    zip?: string;
    country?: string;
}

interface FloatingOrderPanelProps {
    isOpen: boolean;
    onClose: () => void;
    lead: Lead | null;
    defaultAddress?: ShopifyAddress | null;
}

const FloatingOrderPanel: React.FC<FloatingOrderPanelProps> = ({
    isOpen,
    onClose,
    lead,
    defaultAddress
}) => {
    const [isMinimized, setIsMinimized] = useState(false);
    const [position, setPosition] = useState({ x: window.innerWidth - 450, y: 100 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const panelRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Product search state
    const [searchQuery, setSearchQuery] = useState('');
    const [products, setProducts] = useState<Product[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
    const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

    // Discount state
    const [discountMode, setDiscountMode] = useState<'coupon' | 'custom'>('custom');
    const [couponCode, setCouponCode] = useState('');
    const [customDiscountType, setCustomDiscountType] = useState<'percentage' | 'fixed'>('percentage');
    const [customDiscountValue, setCustomDiscountValue] = useState('');
    const [appliedDiscount, setAppliedDiscount] = useState<{ title: string; type: 'percentage' | 'fixed'; value: number } | null>(null);
    const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
    const [couponError, setCouponError] = useState('');

    // Shipping charges state
    const [shippingCharges, setShippingCharges] = useState<string>('');

    // Payment state
    const [paymentType, setPaymentType] = useState<PaymentType>('checkout_url');
    const [orderNotes, setOrderNotes] = useState('');
    const [orderTags, setOrderTags] = useState<TagOption[]>([]);
    const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    // Bucket: pre-filled from what the database knows about the customer's open work; the agent can change it.
    const [bucketOptions, setBucketOptions] = useState<{ bucket_key: string; tag_name: string }[]>([]);
    const [bucketTag, setBucketTag] = useState('');
    const [agentTag, setAgentTag] = useState<string | null>(null);
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    const [checkoutUrl, setCheckoutUrl] = useState('');
    const [urlCopied, setUrlCopied] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [orderError, setOrderError] = useState('');
    const [createdOrder, setCreatedOrder] = useState<{
        id: string;
        uuid?: string;
        order_number?: string;
        name?: string;
        customer_name?: string;
        email?: string;
        phone?: string;
        invoice_url?: string;
        checkout_url?: string;
        status?: string;
        financial_status?: string;
        fulfillment_status?: string;
        total_price?: string;
        subtotal_price?: string;
        currency?: string;
        items_count?: number;
        created_at?: string;
        // Payment info
        amount_paid?: number;
        remaining_balance?: number;
    } | null>(null);

    // Razorpay payment link state
    const [razorpayPaymentLink, setRazorpayPaymentLink] = useState<PaymentLink | null>(null);
    const [razorpayAmount, setRazorpayAmount] = useState<string>(''); // Empty means full amount
    const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
    const [whatsAppSent, setWhatsAppSent] = useState(false);
    const [whatsAppError, setWhatsAppError] = useState('');
    const [isRefreshingOrder, setIsRefreshingOrder] = useState(false);
    const [refreshError, setRefreshError] = useState('');

    // Confirmation modal state
    const [showConfirmation, setShowConfirmation] = useState(false);

    // Shipping address state — prefer lead fields, fall back to shopify customer default_address
    const [shippingAddress, setShippingAddress] = useState({
        address1: lead?.address_1 || defaultAddress?.address1 || '',
        address2: lead?.address_2 || defaultAddress?.address2 || '',
        city: lead?.city || defaultAddress?.city || '',
        province: lead?.province || defaultAddress?.province || '',
        zip: lead?.zip || defaultAddress?.zip || '',
        country: lead?.country || defaultAddress?.country || 'India'
    });

    // Fetch products and tags on mount
    useEffect(() => {
        if (isOpen) {
            if (products.length === 0) {
                fetchProducts();
            }
            if (availableTags.length === 0) {
                fetchCrmTags();
            }
            fetchTagSuggestions();
        }
    }, [isOpen, lead?.uuid]);

    const fetchTagSuggestions = async () => {
        try {
            const response = await apiRequest<{ agent_tag: string | null; bucket: string | null; buckets: { bucket_key: string; tag_name: string }[] }>(
                `/shopify/order-tag-suggestions${lead?.uuid ? `?lead_uuid=${encodeURIComponent(lead.uuid)}` : ''}`);
            const d = response?.data;
            if (!d) return;
            setBucketOptions(d.buckets || []);
            setAgentTag(d.agent_tag || null);
            const suggested = (d.buckets || []).find(b => b.bucket_key === d.bucket);
            setBucketTag(prev => prev || suggested?.tag_name || '');
        } catch (error) {
            console.error('Error fetching tag suggestions:', error);   // the panel works without suggestions
        }
    };

    // Pre-select the agent's own tag once both lists are in; never overrides what the agent chose.
    useEffect(() => {
        if (!agentTag || availableTags.length === 0) return;
        const own = availableTags.find(t => t.value.toLowerCase() === agentTag.toLowerCase());
        if (own) setOrderTags(prev => (prev.length === 0 ? [own] : prev));
    }, [agentTag, availableTags]);

    // Every tag that goes to Shopify: the order tags plus the bucket tag.
    const allTags = () => [...orderTags.map(t => t.value), ...(bucketTag ? [bucketTag] : [])].join(', ');

    // Reset state when panel opens/closes
    useEffect(() => {
        if (isOpen) {
            setPosition({ x: window.innerWidth - 450, y: 20 });
            setIsMinimized(false);
            setOrderSuccess(false);
            setOrderError('');
            setCheckoutUrl('');
            setCreatedOrder(null);
            // Reset address so the lead/defaultAddress useEffect repopulates it fresh
            setShippingAddress({
                address1: lead?.address_1 || defaultAddress?.address1 || '',
                address2: lead?.address_2 || defaultAddress?.address2 || '',
                city: lead?.city || defaultAddress?.city || '',
                province: lead?.province || defaultAddress?.province || '',
                zip: lead?.zip || defaultAddress?.zip || '',
                country: lead?.country || defaultAddress?.country || 'India'
            });
        }
    }, [isOpen]);

    // Tracks which lead the address state currently belongs to, so we can tell a
    // "switched to a different lead" change apart from a "same lead, data refreshed" change.
    const populatedLeadRef = useRef<string | null>(null);

    // Update shipping address when lead or defaultAddress changes.
    // - Different lead → full reset from the new lead (never carry the previous lead's fields;
    //   otherwise a field the new lead leaves empty, e.g. address_2, keeps the old lead's value).
    // - Same lead refreshed → only fill empty fields so in-progress user edits are preserved.
    useEffect(() => {
        if (!lead && !defaultAddress) return;
        const leadKey = lead?.uuid ?? null;
        const isNewLead = leadKey !== populatedLeadRef.current;
        populatedLeadRef.current = leadKey;
        if (isNewLead) {
            setShippingAddress({
                address1: lead?.address_1 || defaultAddress?.address1 || '',
                address2: lead?.address_2 || defaultAddress?.address2 || '',
                city: lead?.city || defaultAddress?.city || '',
                province: lead?.province || defaultAddress?.province || '',
                zip: lead?.zip || defaultAddress?.zip || '',
                country: lead?.country || defaultAddress?.country || 'India'
            });
        } else {
            setShippingAddress(prev => ({
                address1: prev.address1 || lead?.address_1 || defaultAddress?.address1 || '',
                address2: prev.address2 || lead?.address_2 || defaultAddress?.address2 || '',
                city: prev.city || lead?.city || defaultAddress?.city || '',
                province: prev.province || lead?.province || defaultAddress?.province || '',
                zip: prev.zip || lead?.zip || defaultAddress?.zip || '',
                country: prev.country || lead?.country || defaultAddress?.country || 'India'
            }));
        }
    }, [lead, defaultAddress]);

    const fetchProducts = async () => {
        setIsLoadingProducts(true);
        try {
            // Fetch only active products for order creation
            const response = await apiRequest<Product[]>('/shopify/products?status=active');
            if (response.success && response.data) {
                setProducts(response.data);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setIsLoadingProducts(false);
        }
    };

    const fetchCrmTags = async () => {
        setIsLoadingTags(true);
        try {
            const response = await apiRequest<CrmTag[]>('/shopify/crm-tags');
            if (response.success && response.data) {
                // Retired tags stay in the list so their past orders keep
                // counting in reports, but they must not be offered for new
                // ones -- that is the only thing is_active controls now.
                const options: TagOption[] = response.data
                    .filter(tag => tag.is_active !== false)
                    .map(tag => ({
                        value: tag.tag_name,
                        label: tag.tag_name
                    }));
                setAvailableTags(options);
            }
        } catch (error) {
            console.error('Error fetching CRM tags:', error);
        } finally {
            setIsLoadingTags(false);
        }
    };

    // Filter products based on search query
    useEffect(() => {
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            const filtered = products.filter(product =>
                product.title.toLowerCase().includes(query) ||
                product.vendor?.toLowerCase().includes(query) ||
                product.product_type?.toLowerCase().includes(query) ||
                product.variants.some(v =>
                    v.sku?.toLowerCase().includes(query) ||
                    v.title?.toLowerCase().includes(query)
                )
            );
            setFilteredProducts(filtered);
            setShowDropdown(true);
        } else {
            setFilteredProducts([]);
            setShowDropdown(false);
        }
    }, [searchQuery, products]);

    // Handle mouse down on header for dragging
    const handleMouseDown = (e: React.MouseEvent) => {
        if (panelRef.current) {
            const rect = panelRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setIsDragging(true);
        }
    };

    // Handle mouse move for dragging
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                // Calculate panel height (85vh or 850px max)
                const panelHeight = Math.min(window.innerHeight * 0.85, 850);
                const newX = Math.max(0, Math.min(e.clientX - dragOffset.x, window.innerWidth - 420));
                // Allow dragging to top (y=0) but prevent footer from going off screen
                const newY = Math.max(0, Math.min(e.clientY - dragOffset.y, window.innerHeight - panelHeight - 50));
                setPosition({ x: newX, y: newY });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchInputRef.current && !searchInputRef.current.contains(e.target as Node)) {
                // Small delay to allow click on dropdown item
                setTimeout(() => setShowDropdown(false), 150);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Add product to order
    const handleAddProduct = (product: Product, variant: ProductVariant) => {
        // Check if already added
        const existingIndex = selectedProducts.findIndex(
            sp => sp.product.id === product.id && sp.variant.id === variant.id
        );

        if (existingIndex >= 0) {
            // Increment quantity
            const updated = [...selectedProducts];
            updated[existingIndex].quantity += 1;
            setSelectedProducts(updated);
        } else {
            // Add new
            setSelectedProducts([...selectedProducts, { product, variant, quantity: 1 }]);
        }

        // Keep dropdown open so user can add more products
        // Only collapse the expanded product variants
        setExpandedProduct(null);
    };

    // Update quantity
    const handleUpdateQuantity = (index: number, newQuantity: number) => {
        if (newQuantity < 1) return;
        const updated = [...selectedProducts];
        updated[index].quantity = newQuantity;
        setSelectedProducts(updated);
    };

    // Remove product
    const handleRemoveProduct = (index: number) => {
        setSelectedProducts(selectedProducts.filter((_, i) => i !== index));
    };

    // Calculate subtotal
    const subtotal = selectedProducts.reduce((sum, sp) => {
        return sum + (parseFloat(sp.variant.price) * sp.quantity);
    }, 0);

    // Calculate discount amount
    const discountAmount = appliedDiscount
        ? appliedDiscount.type === 'percentage'
            ? (subtotal * appliedDiscount.value) / 100
            : appliedDiscount.value
        : 0;

    // Calculate shipping amount
    const shippingAmount = shippingCharges ? parseFloat(shippingCharges) || 0 : 0;

    // Calculate final total (subtotal - discount + shipping)
    const orderTotal = Math.max(0, subtotal - discountAmount + shippingAmount);

    // Handle coupon validation (placeholder - will connect to API later)
    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;

        setCouponError('');
        setIsValidatingCoupon(true);

        try {
            // TODO: Replace with actual API call to validate Shopify discount code
            // const response = await apiRequest('/shopify/discount-codes/validate', {
            //     method: 'POST',
            //     body: { code: couponCode, subtotal }
            // });

            // Simulated validation for UI demo
            await new Promise(resolve => setTimeout(resolve, 500));

            // For now, show that API needs to be implemented
            setCouponError('Coupon validation API not implemented yet');

        } catch (error) {
            setCouponError('Failed to validate coupon');
        } finally {
            setIsValidatingCoupon(false);
        }
    };

    // Apply custom discount (percentage or fixed)
    const handleApplyCustomDiscount = () => {
        const value = parseFloat(customDiscountValue);
        if (isNaN(value) || value <= 0) {
            setCouponError('Please enter a valid discount value');
            return;
        }

        if (customDiscountType === 'percentage' && value > 100) {
            setCouponError('Percentage cannot exceed 100%');
            return;
        }

        if (customDiscountType === 'fixed' && value > subtotal) {
            setCouponError('Discount cannot exceed subtotal');
            return;
        }

        setAppliedDiscount({
            title: customDiscountType === 'percentage' ? `${value}% OFF` : `₹${value} OFF`,
            type: customDiscountType,
            value: value
        });
        setCouponError('');
    };

    // Remove applied discount
    const handleRemoveDiscount = () => {
        setAppliedDiscount(null);
        setCouponCode('');
        setCustomDiscountValue('');
        setCouponError('');
    };

    // Validate shipping address and tags
    const validateOrderFields = (): string | null => {
        if (!shippingAddress.address1.trim()) return 'Address Line 1 is required';
        if (!shippingAddress.city.trim()) return 'City is required';
        if (!shippingAddress.province.trim()) return 'State/Province is required';
        if (!shippingAddress.zip.trim()) return 'ZIP/Postal Code is required';
        if (!shippingAddress.country.trim()) return 'Country is required';
        if (orderTags.length === 0) return 'At least one order tag is required';
        return null;
    };

    // Handle order creation
    // - checkout_url: Shopify draft order with checkout URL
    // - cod: Shopify order with COD
    // - razorpay_link: ZH order with Razorpay payment link (partial/full)
    const handleCreateOrder = async () => {
        if (selectedProducts.length === 0) return;

        // Validate required fields
        const validationError = validateOrderFields();
        if (validationError) {
            setOrderError(validationError);
            return;
        }

        setIsCreatingOrder(true);
        setOrderError('');
        setOrderSuccess(false);
        setRazorpayPaymentLink(null);
        setWhatsAppSent(false);
        setWhatsAppError('');

        try {
            // Common data preparation
            const lineItems = selectedProducts.map(sp => ({
                variant_id: sp.variant.id,
                quantity: sp.quantity
            }));

            const shippingAddressPayload = {
                first_name: lead?.name?.split(' ')[0] || '',
                last_name: lead?.name?.split(' ').slice(1).join(' ') || '',
                address1: shippingAddress.address1,
                address2: shippingAddress.address2,
                city: shippingAddress.city,
                province: shippingAddress.province,
                zip: shippingAddress.zip,
                country: shippingAddress.country,
                phone: lead?.phone || ''
            };

            const appliedDiscountPayload = appliedDiscount ? {
                title: appliedDiscount.title,
                description: appliedDiscount.type === 'percentage'
                    ? `${appliedDiscount.value}% discount`
                    : `₹${appliedDiscount.value} discount`,
                value_type: appliedDiscount.type === 'percentage' ? 'percentage' : 'fixed_amount',
                value: appliedDiscount.value.toString()
            } : null;

            // Shipping line payload
            const shippingLinePayload = shippingAmount > 0 ? {
                title: 'Shipping',
                price: shippingAmount.toFixed(2)
            } : null;

            const customerPayload = lead?.shopify_customer_id
                ? {
                    id: lead.shopify_customer_id,
                    phone: lead?.phone || ''
                  }
                : {
                    email: lead?.email || '',
                    first_name: lead?.name?.split(' ')[0] || '',
                    last_name: lead?.name?.split(' ').slice(1).join(' ') || '',
                    phone: lead?.phone || ''
                };

            interface OrderResponse {
                id: string;
                order_number?: string;
                name?: string;
                invoice_url?: string;
                checkout_url?: string;
                status?: string;
                financial_status?: string;
                fulfillment_status?: string;
                total_price?: string;
                created_at?: string;
            }

            if (paymentType === 'razorpay_link') {
                // Create Shopify draft order with Razorpay payment link
                // Uses same payload format as Shopify draft orders
                const paymentAmount = razorpayAmount && parseFloat(razorpayAmount) > 0
                    ? parseFloat(razorpayAmount)
                    : undefined; // undefined = full order total

                const response = await createRazorpayOrder({
                    lead_uuid: lead?.uuid,
                    customer: customerPayload,
                    line_items: lineItems,
                    shipping_address: shippingAddressPayload,
                    applied_discount: appliedDiscountPayload,
                    shipping_line: shippingLinePayload,
                    tags: allTags(),
                    note: orderNotes,
                    amount: paymentAmount,
                    expire_in_hours: 72
                });

                if (response.success && response.data) {
                    setOrderSuccess(true);
                    setCreatedOrder({
                        id: response.data.order.uuid,
                        uuid: response.data.order.uuid,
                        order_number: response.data.order.order_number || response.data.order.order_name,
                        name: response.data.order.order_name || response.data.order.order_number,
                        status: response.data.order.status,
                        financial_status: response.data.order.status,
                        total_price: response.data.order.total_price,
                        created_at: response.data.order.created_at
                    });

                    if (response.data.payment_link) {
                        setRazorpayPaymentLink(response.data.payment_link);
                        setCheckoutUrl(response.data.payment_link.short_url);
                    }
                } else {
                    setOrderError(response.message || 'Failed to create order');
                }

            } else if (paymentType === 'checkout_url') {
                // Create Shopify draft order with checkout URL
                const response = await apiRequest<OrderResponse>('/shopify/draft-orders', {
                    method: 'POST',
                    body: {
                        lead_uuid: lead?.uuid,
                        customer: customerPayload,
                        line_items: lineItems,
                        shipping_address: shippingAddressPayload,
                        applied_discount: appliedDiscountPayload,
                        shipping_line: shippingLinePayload,
                        tags: allTags(),
                        note: orderNotes
                    }
                });

                if (response.success && response.data) {
                    setOrderSuccess(true);
                    setCreatedOrder(response.data);
                    // Draft order returns invoice_url for checkout
                    const url = response.data.invoice_url || response.data.checkout_url;
                    if (url) {
                        setCheckoutUrl(url);
                    }
                } else {
                    setOrderError((response as { message?: string }).message || 'Failed to create draft order');
                }

            } else {
                // COD: Create Shopify order
                const response = await apiRequest<OrderResponse>('/shopify/orders', {
                    method: 'POST',
                    body: {
                        lead_uuid: lead?.uuid,
                        customer: customerPayload,
                        line_items: lineItems,
                        shipping_address: shippingAddressPayload,
                        applied_discount: appliedDiscountPayload,
                        shipping_line: shippingLinePayload,
                        tags: allTags(),
                        note: orderNotes,
                        payment_type: 'cod',
                        financial_status: 'pending',
                        transactions: [{
                            kind: 'sale',
                            status: 'pending',
                            gateway: 'Cash on Delivery (COD)'
                        }]
                    }
                });

                if (response.success && response.data) {
                    setOrderSuccess(true);
                    setCreatedOrder(response.data);
                } else {
                    setOrderError((response as { message?: string }).message || 'Failed to create order');
                }
            }

        } catch (error) {
            console.error('Error creating order:', error);
            setOrderError(error instanceof Error ? error.message : 'Failed to create order. Please try again.');
        } finally {
            setIsCreatingOrder(false);
        }
    };

    // Send payment link via WhatsApp (Razorpay)
    const handleSendWhatsApp = async () => {
        if (!razorpayPaymentLink?.payment_link_id) return;

        setIsSendingWhatsApp(true);
        setWhatsAppError('');

        try {
            const response = await sendPaymentLinkWhatsApp(razorpayPaymentLink.payment_link_id);

            if (response.success) {
                setWhatsAppSent(true);
            } else {
                setWhatsAppError(response.error || 'Failed to send WhatsApp');
            }
        } catch (error) {
            setWhatsAppError('Failed to send WhatsApp message');
            console.error('Send WhatsApp error:', error);
        } finally {
            setIsSendingWhatsApp(false);
        }
    };

    // Send checkout URL via WhatsApp
    const handleSendCheckoutWhatsApp = async () => {
        if (!checkoutUrl || !lead?.phone) {
            setWhatsAppError('Phone number not available');
            return;
        }

        setIsSendingWhatsApp(true);
        setWhatsAppError('');

        try {
            // Sends from our own WABA (Meta Cloud API) and logs to the lead's
            // WhatsApp Inbox thread — lead_uuid is what attributes it.
            const response = await apiRequest('/api/razorpay/send-whatsapp-template', {
                method: 'POST',
                body: JSON.stringify({
                    phone: lead.phone,
                    template_id: 'crm_1',
                    lead_uuid: lead?.uuid,
                    parameters: [
                        lead.name || 'Customer',
                        checkoutUrl
                    ]
                })
            });

            if (response.success) {
                setWhatsAppSent(true);
            } else {
                setWhatsAppError(response.error || 'Failed to send WhatsApp');
            }
        } catch (error) {
            setWhatsAppError('Failed to send WhatsApp message');
            console.error('Send WhatsApp error:', error);
        } finally {
            setIsSendingWhatsApp(false);
        }
    };

    // Reset form after successful order
    const handleResetForm = () => {
        setSelectedProducts([]);
        setAppliedDiscount(null);
        setCustomDiscountValue('');
        setCouponCode('');
        setShippingCharges('');
        setOrderNotes('');
        setOrderTags([]);
        setBucketTag('');
        setOrderSuccess(false);
        setOrderError('');
        setCheckoutUrl('');
        setCreatedOrder(null);
        setRazorpayPaymentLink(null);
        setRazorpayAmount('');
        setWhatsAppSent(false);
        setWhatsAppError('');
        setRefreshError('');
    };

    // Refresh order status from Shopify
    const handleRefreshOrder = async () => {
        if (!createdOrder?.uuid) return;

        setIsRefreshingOrder(true);
        setRefreshError('');

        try {
            const response = await refreshOrderStatus(createdOrder.uuid);

            if (response.success && response.data) {
                const orderData = response.data.order;
                const paymentData = response.data.payment_link;

                // Calculate remaining balance
                // amount_paid from API is in paise, convert to INR
                const totalPrice = orderData.total_price || 0;
                const amountPaidInr = paymentData ? (paymentData.amount_paid || 0) / 100 : 0;
                const remainingBalance = Math.max(0, totalPrice - amountPaidInr);

                // Update the created order with fresh data
                setCreatedOrder(prev => prev ? {
                    ...prev,
                    order_number: orderData.order_name || orderData.order_number?.toString() || prev.order_number,
                    name: orderData.order_name || prev.name,
                    financial_status: orderData.financial_status,
                    fulfillment_status: orderData.fulfillment_status || undefined,
                    status: orderData.order_status,
                    total_price: totalPrice.toString(),
                    items_count: orderData.line_items_count || prev.items_count,
                    amount_paid: amountPaidInr,
                    remaining_balance: remainingBalance
                } : null);

                // Update payment link if available
                if (paymentData && razorpayPaymentLink) {
                    setRazorpayPaymentLink(prev => prev ? {
                        ...prev,
                        status: paymentData.status as PaymentLink['status'],
                        amount: prev.amount // Keep original amount
                    } : null);
                }
            } else {
                setRefreshError(response.error || 'Failed to refresh order');
            }
        } catch (error) {
            setRefreshError('Failed to refresh order status');
            console.error('Refresh order error:', error);
        } finally {
            setIsRefreshingOrder(false);
        }
    };

    // Copy checkout URL
    const handleCopyUrl = async () => {
        if (!checkoutUrl) return;
        try {
            await navigator.clipboard.writeText(checkoutUrl);
            setUrlCopied(true);
            setTimeout(() => setUrlCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy URL:', error);
        }
    };

    // Show confirmation modal before placing order
    const handleShowConfirmation = () => {
        if (selectedProducts.length === 0) return;

        // Validate required fields before showing confirmation
        const validationError = validateOrderFields();
        if (validationError) {
            setOrderError(validationError);
            return;
        }

        setOrderError(''); // Clear any previous errors
        setShowConfirmation(true);
    };

    // Confirm and place order
    const handleConfirmOrder = () => {
        setShowConfirmation(false);
        handleCreateOrder();
    };

    // Cancel confirmation
    const handleCancelConfirmation = () => {
        setShowConfirmation(false);
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            ref={panelRef}
            className={`fixed z-[9999] bg-white rounded-lg shadow-2xl border border-gray-200 transition-all duration-200 ${
                isDragging ? 'cursor-grabbing' : ''
            }`}
            style={{
                left: position.x,
                top: position.y,
                width: isMinimized ? '280px' : '420px',
            }}
        >
            {/* Header - Draggable */}
            <div
                className={`flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg cursor-grab ${
                    isDragging ? 'cursor-grabbing' : ''
                }`}
                onMouseDown={handleMouseDown}
            >
                <div className="flex items-center gap-2">
                    <GripHorizontal className="h-4 w-4 opacity-60" />
                    <ShoppingCart className="h-5 w-5" />
                    <span className="font-semibold">Create Order</span>
                    {selectedProducts.length > 0 && (
                        <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                            {selectedProducts.length} item{selectedProducts.length > 1 ? 's' : ''}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setIsMinimized(!isMinimized)}
                        className="p-1.5 hover:bg-white/20 rounded transition-colors"
                        title={isMinimized ? 'Expand' : 'Minimize'}
                    >
                        {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-white/20 rounded transition-colors"
                        title="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Content - Only show when not minimized */}
            {!isMinimized && (
                <div className="flex flex-col relative" style={{ height: '85vh', maxHeight: '850px' }}>
                    {/* Success Overlay */}
                    {orderSuccess && createdOrder && (
                        <div className="absolute inset-0 z-50 bg-white/95 backdrop-blur-sm flex flex-col">
                            {/* Success Header */}
                            <div className={`px-5 py-4 ${
                                paymentType === 'razorpay_link'
                                    ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                    : paymentType === 'cod'
                                    ? 'bg-gradient-to-r from-orange-500 to-amber-500'
                                    : 'bg-gradient-to-r from-indigo-500 to-purple-500'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white/20 rounded-full">
                                            <CheckCircle className="h-6 w-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white">
                                                {paymentType === 'razorpay_link' ? 'Order Created!' :
                                                 paymentType === 'checkout_url' ? 'Draft Order Created!' :
                                                 'COD Order Created!'}
                                            </h3>
                                            <p className="text-white/80 text-sm">
                                                {paymentType === 'razorpay_link'
                                                    ? 'Payment link is ready'
                                                    : paymentType === 'checkout_url'
                                                    ? 'Checkout URL is ready to share'
                                                    : 'Order placed successfully'}
                                            </p>
                                        </div>
                                    </div>
                                    {/* Refresh Button */}
                                    <button
                                        onClick={handleRefreshOrder}
                                        disabled={isRefreshingOrder}
                                        className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                                        title="Refresh order status"
                                    >
                                        <RefreshCw className={`h-5 w-5 text-white ${isRefreshingOrder ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>
                                {refreshError && (
                                    <p className="text-white/80 text-xs mt-2 flex items-center gap-1">
                                        <AlertCircle className="h-3 w-3" />
                                        {refreshError}
                                    </p>
                                )}
                            </div>

                            {/* Success Content */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {/* Order Info Card */}
                                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                                    {/* Order Number - Prominent */}
                                    <div className="text-center pb-3 border-b border-gray-200">
                                        <span className="text-gray-500 text-xs uppercase tracking-wide">Order Number</span>
                                        <p className="font-bold text-2xl text-gray-900 mt-1">
                                            {createdOrder.order_number || createdOrder.name || `#${createdOrder.id}`}
                                        </p>
                                    </div>

                                    {/* Customer Info */}
                                    {(createdOrder.customer_name || lead?.name) && (
                                        <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                                            <User className="h-4 w-4 text-gray-400" />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 truncate">
                                                    {createdOrder.customer_name || lead?.name}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">
                                                    {createdOrder.phone || lead?.phone}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Order Details Grid */}
                                    <div className="grid grid-cols-2 gap-2">
                                        {/* Total Amount */}
                                        <div className="p-2 bg-white rounded-lg">
                                            <span className="text-gray-500 text-xs">Total Amount</span>
                                            <p className="font-bold text-lg text-gray-900">
                                                ₹{createdOrder.total_price || orderTotal.toFixed(2)}
                                            </p>
                                        </div>

                                        {/* Items Count */}
                                        <div className="p-2 bg-white rounded-lg">
                                            <span className="text-gray-500 text-xs">Items</span>
                                            <p className="font-semibold text-gray-900">
                                                {createdOrder.items_count || selectedProducts.length} item{(createdOrder.items_count || selectedProducts.length) !== 1 ? 's' : ''}
                                            </p>
                                        </div>

                                        {/* Amount Paid - Show for partial payments */}
                                        {paymentType === 'razorpay_link' && (createdOrder.amount_paid !== undefined || razorpayPaymentLink) && (
                                            <div className="p-2 bg-green-50 rounded-lg border border-green-200">
                                                <span className="text-green-600 text-xs">Amount Paid</span>
                                                <p className="font-bold text-lg text-green-700">
                                                    ₹{(createdOrder.amount_paid || 0).toLocaleString('en-IN')}
                                                </p>
                                            </div>
                                        )}

                                        {/* Remaining Balance - Show for partial payments */}
                                        {paymentType === 'razorpay_link' && createdOrder.remaining_balance !== undefined && createdOrder.remaining_balance > 0 && (
                                            <div className="p-2 bg-orange-50 rounded-lg border border-orange-200">
                                                <span className="text-orange-600 text-xs">Remaining</span>
                                                <p className="font-bold text-lg text-orange-700">
                                                    ₹{createdOrder.remaining_balance.toLocaleString('en-IN')}
                                                </p>
                                            </div>
                                        )}

                                        {/* Payment Status */}
                                        <div className="p-2 bg-white rounded-lg">
                                            <span className="text-gray-500 text-xs">Payment</span>
                                            <p className={`font-semibold capitalize ${
                                                createdOrder.financial_status === 'paid' ? 'text-green-600' :
                                                createdOrder.financial_status === 'pending' ? 'text-yellow-600' :
                                                createdOrder.financial_status === 'partially_paid' ? 'text-orange-600' :
                                                'text-orange-600'
                                            }`}>
                                                {createdOrder.financial_status === 'partially_paid' ? 'Partially Paid' :
                                                 createdOrder.financial_status || 'Pending'}
                                            </p>
                                        </div>

                                        {/* Order Status */}
                                        <div className="p-2 bg-white rounded-lg">
                                            <span className="text-gray-500 text-xs">Status</span>
                                            <p className={`font-semibold capitalize ${
                                                createdOrder.status === 'open' ? 'text-blue-600' :
                                                createdOrder.status === 'completed' ? 'text-green-600' :
                                                'text-gray-900'
                                            }`}>
                                                {createdOrder.status || 'Open'}
                                            </p>
                                        </div>

                                        {/* Fulfillment Status */}
                                        {createdOrder.fulfillment_status !== undefined && (
                                            <div className="p-2 bg-white rounded-lg">
                                                <span className="text-gray-500 text-xs">Fulfillment</span>
                                                <p className={`font-semibold capitalize ${
                                                    createdOrder.fulfillment_status === 'fulfilled' ? 'text-green-600' :
                                                    createdOrder.fulfillment_status === 'partial' ? 'text-yellow-600' :
                                                    'text-gray-600'
                                                }`}>
                                                    {createdOrder.fulfillment_status || 'Unfulfilled'}
                                                </p>
                                            </div>
                                        )}

                                        {/* Order Type */}
                                        <div className="p-2 bg-white rounded-lg">
                                            <span className="text-gray-500 text-xs">Type</span>
                                            <p className="font-semibold text-gray-900 capitalize">
                                                {paymentType === 'razorpay_link' ? 'Partial Payment' :
                                                 paymentType === 'checkout_url' ? 'Checkout' : 'COD'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Created Time */}
                                    {createdOrder.created_at && (
                                        <div className="text-center pt-2 border-t border-gray-200">
                                            <span className="text-xs text-gray-400">
                                                Created {new Date(createdOrder.created_at).toLocaleString()}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Razorpay Payment Link Section */}
                                {paymentType === 'razorpay_link' && razorpayPaymentLink && (
                                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl space-y-3">
                                        <div className="flex items-center gap-2">
                                            <IndianRupee className="h-5 w-5 text-green-600" />
                                            <span className="font-medium text-green-800">Payment Link</span>
                                            {razorpayPaymentLink.amount && (
                                                <span className="ml-auto text-sm font-bold text-green-700">
                                                    ₹{razorpayPaymentLink.amount.toLocaleString('en-IN')}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={razorpayPaymentLink.short_url}
                                                readOnly
                                                className="flex-1 px-3 py-2 bg-white border border-green-300 rounded-lg text-sm text-gray-700"
                                            />
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(razorpayPaymentLink.short_url);
                                                    setUrlCopied(true);
                                                    setTimeout(() => setUrlCopied(false), 2000);
                                                }}
                                                className="px-3 py-2 bg-white border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
                                            >
                                                {urlCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-500" />}
                                            </button>
                                        </div>
                                        <button
                                            onClick={handleSendWhatsApp}
                                            disabled={isSendingWhatsApp || whatsAppSent}
                                            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                                                whatsAppSent
                                                    ? 'bg-green-600 text-white'
                                                    : 'bg-green-600 hover:bg-green-700 text-white'
                                            }`}
                                        >
                                            {isSendingWhatsApp ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : whatsAppSent ? (
                                                <>
                                                    <Check className="h-4 w-4" />
                                                    WhatsApp Sent!
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="h-4 w-4" />
                                                    Send via WhatsApp
                                                </>
                                            )}
                                        </button>
                                        {whatsAppError && (
                                            <p className="text-xs text-red-600 flex items-center gap-1">
                                                <AlertCircle className="h-3 w-3" />
                                                {whatsAppError}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Checkout URL Section */}
                                {paymentType === 'checkout_url' && checkoutUrl && (
                                    <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl space-y-3">
                                        <div className="flex items-center gap-2">
                                            <Link2 className="h-5 w-5 text-indigo-600" />
                                            <span className="font-medium text-indigo-800">Checkout URL</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={checkoutUrl}
                                                readOnly
                                                className="flex-1 px-3 py-2 bg-white border border-indigo-300 rounded-lg text-sm text-gray-700"
                                            />
                                            <button
                                                onClick={handleCopyUrl}
                                                className="px-3 py-2 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-50 transition-colors"
                                            >
                                                {urlCopied ? <Check className="h-4 w-4 text-indigo-600" /> : <Copy className="h-4 w-4 text-gray-500" />}
                                            </button>
                                        </div>
                                        <button
                                            onClick={handleSendCheckoutWhatsApp}
                                            disabled={isSendingWhatsApp || whatsAppSent}
                                            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                                                whatsAppSent
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                            }`}
                                        >
                                            {isSendingWhatsApp ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : whatsAppSent ? (
                                                <>
                                                    <Check className="h-4 w-4" />
                                                    WhatsApp Sent!
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="h-4 w-4" />
                                                    Send via WhatsApp
                                                </>
                                            )}
                                        </button>
                                        {whatsAppError && (
                                            <p className="text-xs text-red-600 flex items-center gap-1">
                                                <AlertCircle className="h-3 w-3" />
                                                {whatsAppError}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* COD Section */}
                                {paymentType === 'cod' && (
                                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                                        <div className="flex items-center gap-2">
                                            <Truck className="h-5 w-5 text-orange-600" />
                                            <span className="font-medium text-orange-800">Cash on Delivery</span>
                                        </div>
                                        <p className="text-sm text-orange-700 mt-2">
                                            Order has been placed. Payment will be collected on delivery.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Success Footer */}
                            <div className="px-4 py-3 bg-gray-50 border-t flex gap-2">
                                <button
                                    onClick={handleResetForm}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                                >
                                    <RefreshCw className="h-4 w-4" />
                                    New Order
                                </button>
                                <button
                                    onClick={() => {
                                        handleResetForm();
                                        onClose();
                                    }}
                                    className="flex-1 px-4 py-2.5 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Scrollable Content Area */}
                    <div className="flex-1 overflow-y-auto">
                        {/* Customer Summary */}
                        <div className="px-4 py-3 bg-gray-50 border-b">
                        <div className="flex items-center gap-2 text-sm">
                            <User className="h-4 w-4 text-gray-500" />
                            <span className="font-medium text-gray-700">{lead?.name || 'Unknown'}</span>
                            {lead?.phone && (
                                <span className="text-gray-500">| {lead.phone}</span>
                            )}
                        </div>
                        {lead?.email && (
                            <p className="text-xs text-gray-500 mt-1 ml-6">{lead.email}</p>
                        )}
                    </div>

                    {/* Order Form Sections */}
                    <div className="p-4 space-y-4">
                        {/* Products Section */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                                <Package className="h-4 w-4 text-indigo-500" />
                                Products
                            </h4>

                            {/* Product Search */}
                            <div className="relative" ref={searchInputRef}>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onFocus={() => searchQuery && setShowDropdown(true)}
                                        placeholder="Search products by name, SKU..."
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                    {isLoadingProducts && (
                                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                                    )}
                                </div>

                                {/* Search Results Dropdown */}
                                {showDropdown && filteredProducts.length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                        {filteredProducts.map((product) => (
                                            <div key={product.id} className="border-b border-gray-100 last:border-0">
                                                {/* Product Header */}
                                                <div
                                                    className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => setExpandedProduct(
                                                        expandedProduct === product.id ? null : product.id
                                                    )}
                                                >
                                                    {/* Product Image */}
                                                    <div className="w-10 h-10 bg-gray-100 rounded flex-shrink-0 flex items-center justify-center overflow-hidden">
                                                        {product.image?.src ? (
                                                            <img
                                                                src={product.image.src}
                                                                alt={product.title}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <ImageIcon className="h-5 w-5 text-gray-400" />
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {product.title}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {product.variants.length} variant{product.variants.length > 1 ? 's' : ''} • {product.vendor}
                                                        </p>
                                                        {(() => {
                                                            // Combos live in the variant name ("... + Shilajit gold"), so show which
                                                            // variants the search matched without making the agent expand the product.
                                                            const q = searchQuery.trim().toLowerCase();
                                                            const hits = q ? product.variants.filter(v => v.title?.toLowerCase().includes(q)) : [];
                                                            return hits.length > 0 && !product.title.toLowerCase().includes(q) ? (
                                                                <p className="text-xs text-indigo-600 truncate">
                                                                    Matches: {hits.map(v => `${v.title} (₹${parseFloat(v.price).toFixed(0)})`).join(', ')}
                                                                </p>
                                                            ) : null;
                                                        })()}
                                                    </div>
                                                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${
                                                        expandedProduct === product.id ? 'rotate-180' : ''
                                                    }`} />
                                                </div>

                                                {/* Variants - Show when expanded */}
                                                {expandedProduct === product.id && (
                                                    <div className="bg-gray-50 px-3 pb-2">
                                                        {product.variants.map((variant) => (
                                                            <div
                                                                key={variant.id}
                                                                className="flex items-center justify-between py-2 px-2 hover:bg-white rounded cursor-pointer"
                                                                onClick={() => handleAddProduct(product, variant)}
                                                            >
                                                                <div>
                                                                    <p className="text-sm text-gray-700">
                                                                        {variant.title === 'Default Title' ? 'Default' : variant.title}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {variant.sku && `SKU: ${variant.sku} • `}
                                                                        Stock: {variant.inventory_quantity}
                                                                    </p>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-sm font-medium text-gray-900">
                                                                        ₹{parseFloat(variant.price).toFixed(2)}
                                                                    </span>
                                                                    <button className="p-1 bg-indigo-100 text-indigo-600 rounded hover:bg-indigo-200">
                                                                        <Plus className="h-4 w-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* No results */}
                                {showDropdown && searchQuery && filteredProducts.length === 0 && !isLoadingProducts && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
                                        No products found
                                    </div>
                                )}
                            </div>

                            {/* Selected Products List */}
                            {selectedProducts.length > 0 ? (
                                <div className="mt-3 space-y-2">
                                    {selectedProducts.map((sp, index) => (
                                        <div
                                            key={`${sp.product.id}-${sp.variant.id}`}
                                            className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                                        >
                                            {/* Product Image */}
                                            <div className="w-10 h-10 bg-white rounded flex-shrink-0 flex items-center justify-center overflow-hidden border">
                                                {sp.variant.image_url || sp.product.image?.src ? (
                                                    <img
                                                        src={sp.variant.image_url || sp.product.image?.src}
                                                        alt={sp.product.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <ImageIcon className="h-5 w-5 text-gray-400" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {sp.product.title}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {sp.variant.title !== 'Default Title' && sp.variant.title}
                                                    {sp.variant.title !== 'Default Title' && ' • '}
                                                    ₹{parseFloat(sp.variant.price).toFixed(2)} each
                                                </p>
                                            </div>
                                            {/* Quantity Controls */}
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleUpdateQuantity(index, sp.quantity - 1)}
                                                    className="w-6 h-6 flex items-center justify-center bg-white border rounded text-gray-600 hover:bg-gray-100"
                                                    disabled={sp.quantity <= 1}
                                                >
                                                    -
                                                </button>
                                                <span className="w-8 text-center text-sm font-medium">
                                                    {sp.quantity}
                                                </span>
                                                <button
                                                    onClick={() => handleUpdateQuantity(index, sp.quantity + 1)}
                                                    className="w-6 h-6 flex items-center justify-center bg-white border rounded text-gray-600 hover:bg-gray-100"
                                                >
                                                    +
                                                </button>
                                            </div>
                                            {/* Price */}
                                            <span className="text-sm font-medium text-gray-900 w-16 text-right">
                                                ₹{(parseFloat(sp.variant.price) * sp.quantity).toFixed(2)}
                                            </span>
                                            {/* Remove */}
                                            <button
                                                onClick={() => handleRemoveProduct(index)}
                                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="mt-3 text-xs text-gray-400 text-center py-2">
                                    Search and add products above
                                </div>
                            )}
                        </div>

                        {/* Shipping Address */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                                <MapPin className="h-4 w-4 text-indigo-500" />
                                Shipping Address
                                <span className="text-red-500">*</span>
                            </h4>
                            <div className="space-y-2">
                                <input
                                    type="text"
                                    placeholder="Address Line 1 *"
                                    value={shippingAddress.address1}
                                    onChange={(e) => setShippingAddress(prev => ({ ...prev, address1: e.target.value }))}
                                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${!shippingAddress.address1.trim() && orderError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                    required
                                />
                                <input
                                    type="text"
                                    placeholder="Address Line 2"
                                    value={shippingAddress.address2}
                                    onChange={(e) => setShippingAddress(prev => ({ ...prev, address2: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                />
                                <div className="grid grid-cols-2 gap-2">
                                    <input
                                        type="text"
                                        placeholder="City *"
                                        value={shippingAddress.city}
                                        onChange={(e) => setShippingAddress(prev => ({ ...prev, city: e.target.value }))}
                                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${!shippingAddress.city.trim() && orderError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                        required
                                    />
                                    <input
                                        type="text"
                                        placeholder="State/Province *"
                                        value={shippingAddress.province}
                                        onChange={(e) => setShippingAddress(prev => ({ ...prev, province: e.target.value }))}
                                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${!shippingAddress.province.trim() && orderError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <input
                                        type="text"
                                        placeholder="ZIP/Postal Code *"
                                        value={shippingAddress.zip}
                                        onChange={(e) => setShippingAddress(prev => ({ ...prev, zip: e.target.value }))}
                                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${!shippingAddress.zip.trim() && orderError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                        required
                                    />
                                    <input
                                        type="text"
                                        placeholder="Country *"
                                        value={shippingAddress.country}
                                        onChange={(e) => setShippingAddress(prev => ({ ...prev, country: e.target.value }))}
                                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${!shippingAddress.country.trim() && orderError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Discount/Coupon Section */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                                <BadgePercent className="h-4 w-4 text-indigo-500" />
                                Discount
                            </h4>

                            {appliedDiscount ? (
                                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <Tag className="h-4 w-4 text-green-600" />
                                        <div>
                                            <span className="text-sm font-medium text-green-800">{appliedDiscount.title}</span>
                                            <p className="text-xs text-green-600">
                                                -₹{discountAmount.toFixed(2)} off
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleRemoveDiscount}
                                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {/* Discount Mode Toggle */}
                                    <div className="flex bg-gray-100 rounded-lg p-1">
                                        <button
                                            onClick={() => setDiscountMode('custom')}
                                            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                                discountMode === 'custom'
                                                    ? 'bg-white text-indigo-700 shadow-sm'
                                                    : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                        >
                                            Custom Discount
                                        </button>
                                        <button
                                            onClick={() => setDiscountMode('coupon')}
                                            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                                discountMode === 'coupon'
                                                    ? 'bg-white text-indigo-700 shadow-sm'
                                                    : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                        >
                                            Coupon Code
                                        </button>
                                    </div>

                                    {discountMode === 'custom' ? (
                                        <div className="flex gap-2">
                                            {/* Discount Type Toggle */}
                                            <div className="flex bg-gray-100 rounded-lg p-0.5">
                                                <button
                                                    onClick={() => setCustomDiscountType('percentage')}
                                                    className={`px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                                        customDiscountType === 'percentage'
                                                            ? 'bg-white text-indigo-700 shadow-sm'
                                                            : 'text-gray-600'
                                                    }`}
                                                >
                                                    %
                                                </button>
                                                <button
                                                    onClick={() => setCustomDiscountType('fixed')}
                                                    className={`px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                                        customDiscountType === 'fixed'
                                                            ? 'bg-white text-indigo-700 shadow-sm'
                                                            : 'text-gray-600'
                                                    }`}
                                                >
                                                    ₹
                                                </button>
                                            </div>
                                            <input
                                                type="number"
                                                value={customDiscountValue}
                                                onChange={(e) => {
                                                    setCustomDiscountValue(e.target.value);
                                                    setCouponError('');
                                                }}
                                                placeholder={customDiscountType === 'percentage' ? 'Enter %' : 'Enter amount'}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                                min="0"
                                                max={customDiscountType === 'percentage' ? '100' : undefined}
                                            />
                                            <button
                                                onClick={handleApplyCustomDiscount}
                                                disabled={!customDiscountValue || subtotal === 0}
                                                className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={couponCode}
                                                onChange={(e) => {
                                                    setCouponCode(e.target.value.toUpperCase());
                                                    setCouponError('');
                                                }}
                                                placeholder="Enter coupon code"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent uppercase"
                                            />
                                            <button
                                                onClick={handleApplyCoupon}
                                                disabled={!couponCode.trim() || isValidatingCoupon}
                                                className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                            >
                                                {isValidatingCoupon ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    'Apply'
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {couponError && (
                                <p className="text-xs text-red-500 mt-1">{couponError}</p>
                            )}
                        </div>

                        {/* Shipping Charges */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                                <Truck className="h-4 w-4 text-indigo-500" />
                                Shipping Charges
                            </h4>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                                <input
                                    type="number"
                                    value={shippingCharges}
                                    onChange={(e) => setShippingCharges(e.target.value)}
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                />
                            </div>
                            <p className="text-xs text-gray-400 mt-1">Leave empty or 0 for free shipping</p>
                        </div>

                        {/* Payment Type Selection */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                                <CreditCard className="h-4 w-4 text-indigo-500" />
                                Payment Method
                            </h4>
                            <div className="grid grid-cols-3 gap-2">
                                <button
                                    onClick={() => setPaymentType('checkout_url')}
                                    className={`p-2.5 rounded-lg border-2 text-left transition-all ${
                                        paymentType === 'checkout_url'
                                            ? 'border-indigo-500 bg-indigo-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-1.5">
                                        <Link2 className={`h-3.5 w-3.5 ${paymentType === 'checkout_url' ? 'text-indigo-600' : 'text-gray-400'}`} />
                                        <span className={`text-xs font-medium ${paymentType === 'checkout_url' ? 'text-indigo-700' : 'text-gray-700'}`}>
                                            Prepaid
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-gray-500 mt-0.5 ml-5">Checkout URL</p>
                                </button>
                                <button
                                    onClick={() => setPaymentType('razorpay_link')}
                                    className={`p-2.5 rounded-lg border-2 text-left transition-all ${
                                        paymentType === 'razorpay_link'
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-1.5">
                                        <IndianRupee className={`h-3.5 w-3.5 ${paymentType === 'razorpay_link' ? 'text-green-600' : 'text-gray-400'}`} />
                                        <span className={`text-xs font-medium ${paymentType === 'razorpay_link' ? 'text-green-700' : 'text-gray-700'}`}>
                                            Partial Paid
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-gray-500 mt-0.5 ml-5">Razorpay link</p>
                                </button>
                                <button
                                    onClick={() => setPaymentType('cod')}
                                    className={`p-2.5 rounded-lg border-2 text-left transition-all ${
                                        paymentType === 'cod'
                                            ? 'border-orange-500 bg-orange-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-1.5">
                                        <Truck className={`h-3.5 w-3.5 ${paymentType === 'cod' ? 'text-orange-600' : 'text-gray-400'}`} />
                                        <span className={`text-xs font-medium ${paymentType === 'cod' ? 'text-orange-700' : 'text-gray-700'}`}>
                                            COD
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-gray-500 mt-0.5 ml-5 whitespace-nowrap">Cash on Delivery</p>
                                </button>
                            </div>

                            {/* Razorpay Amount Input - shown only when Razorpay is selected */}
                            {paymentType === 'razorpay_link' && (
                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <label className="block text-xs font-medium text-green-700 mb-1.5">
                                        Payment Amount (leave empty for full amount)
                                    </label>
                                    <div className="relative">
                                        <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                        <input
                                            type="number"
                                            value={razorpayAmount}
                                            onChange={(e) => setRazorpayAmount(e.target.value)}
                                            placeholder={orderTotal.toFixed(2)}
                                            min="1"
                                            max={orderTotal}
                                            step="0.01"
                                            className="w-full pl-8 pr-3 py-2 border border-green-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        />
                                    </div>
                                    <p className="text-[10px] text-green-600 mt-1">
                                        Order Total: Rs.{orderTotal.toFixed(2)} |
                                        Payment: Rs.{razorpayAmount && parseFloat(razorpayAmount) > 0 ? parseFloat(razorpayAmount).toFixed(2) : orderTotal.toFixed(2)}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Order Tags */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
                                <Tag className="h-4 w-4 text-indigo-500" />
                                Order Tags
                                <span className="text-red-500">*</span>
                            </h4>
                            <Select<TagOption, true>
                                isMulti
                                value={orderTags}
                                onChange={(newValue) => setOrderTags(newValue as TagOption[])}
                                options={availableTags}
                                isLoading={isLoadingTags}
                                placeholder="Select tags... *"
                                noOptionsMessage={() => "No tags available"}
                                classNames={{
                                    control: (state) =>
                                        `!min-h-[38px] !shadow-none ${orderTags.length === 0 && orderError ? '!border-red-300 !bg-red-50' : '!border-gray-300'} ${state.isFocused ? '!border-indigo-500 !ring-2 !ring-indigo-500' : ''}`,
                                    placeholder: () => '!text-gray-400 !text-sm',
                                    input: () => '!text-sm',
                                    option: () => '!text-sm',
                                    multiValue: () => '!bg-indigo-100 !rounded',
                                    multiValueLabel: () => '!text-indigo-800 !text-xs !py-0.5 !px-1',
                                    multiValueRemove: () => '!text-indigo-600 hover:!bg-indigo-200 hover:!text-indigo-800 !rounded-r',
                                    menu: () => '!z-20',
                                }}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: '0.375rem',
                                    }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                                menuPortalTarget={document.body}
                            />
                            <p className="text-xs text-gray-400 mt-1">Required: Select at least one tag</p>
                        </div>

                        {/* Bucket */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
                                <Tag className="h-4 w-4 text-violet-500" />
                                Bucket
                            </h4>
                            <select
                                value={bucketTag}
                                onChange={(e) => setBucketTag(e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-2.5 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">Automatic (from the customer&apos;s open work)</option>
                                {bucketOptions.map(b => <option key={b.bucket_key} value={b.tag_name}>{b.tag_name}</option>)}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Filled in for you from what this customer has open. Change it only if the order belongs elsewhere.</p>
                        </div>

                        {/* Order Notes */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Order Notes</h4>
                            <textarea
                                value={orderNotes}
                                onChange={(e) => setOrderNotes(e.target.value)}
                                placeholder="Add notes for this order..."
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                            />
                        </div>
                    </div>
                    </div>

                    {/* Fixed Footer with Order Summary */}
                    <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-t rounded-b-lg">
                        {!orderSuccess && (
                            <>
                                {/* Order Summary */}
                                <div className="space-y-1 mb-3">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">Subtotal</span>
                                        <span className="text-gray-900">₹{subtotal.toFixed(2)}</span>
                                    </div>
                                    {appliedDiscount && (
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-green-600 flex items-center gap-1">
                                                <Tag className="h-3 w-3" />
                                                {appliedDiscount.title}
                                            </span>
                                            <span className="text-green-600">-₹{discountAmount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {shippingAmount > 0 && (
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600 flex items-center gap-1">
                                                <Truck className="h-3 w-3" />
                                                Shipping
                                            </span>
                                            <span className="text-gray-900">+₹{shippingAmount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                        <span className="font-semibold text-gray-900">Total</span>
                                        <span className="text-lg font-bold text-gray-900">₹{orderTotal.toFixed(2)}</span>
                                    </div>
                                </div>

                                {/* Error Message */}
                                {orderError && (
                                    <div className="mb-3 flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                                        <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                        <p className="text-xs text-red-700">{orderError}</p>
                                    </div>
                                )}

                                {/* Action Button */}
                                <button
                                    onClick={handleShowConfirmation}
                                    disabled={selectedProducts.length === 0 || isCreatingOrder}
                                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                        paymentType === 'razorpay_link'
                                            ? 'bg-green-600 hover:bg-green-700'
                                            : paymentType === 'cod'
                                            ? 'bg-orange-600 hover:bg-orange-700'
                                            : 'bg-indigo-600 hover:bg-indigo-700'
                                    }`}
                                >
                                    {isCreatingOrder ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Creating Order...
                                        </>
                                    ) : paymentType === 'razorpay_link' ? (
                                        <>
                                            <IndianRupee className="h-4 w-4" />
                                            Generate Payment Link
                                        </>
                                    ) : paymentType === 'checkout_url' ? (
                                        <>
                                            <Link2 className="h-4 w-4" />
                                            Generate Checkout URL
                                        </>
                                    ) : (
                                        <>
                                            <Truck className="h-4 w-4" />
                                            Create COD Order
                                        </>
                                    )}
                                </button>
                                {selectedProducts.length === 0 && !orderError && (
                                    <p className="text-xs text-gray-400 text-center mt-2">
                                        Add products to create an order
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Minimized state info */}
            {isMinimized && (
                <div className="px-4 py-2 text-sm text-gray-600 flex items-center justify-between">
                    <span>
                        {selectedProducts.length > 0
                            ? `${selectedProducts.length} item${selectedProducts.length > 1 ? 's' : ''} • ₹${orderTotal.toFixed(2)}`
                            : 'Order in progress...'
                        }
                    </span>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmation && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={handleCancelConfirmation}
                    />

                    {/* Modal */}
                    <div className="relative bg-white rounded-xl shadow-2xl w-[380px] max-h-[80vh] overflow-hidden">
                        {/* Header */}
                        <div className={`px-5 py-4 ${
                            paymentType === 'razorpay_link'
                                ? 'bg-gradient-to-r from-green-600 to-emerald-600'
                                : paymentType === 'cod'
                                ? 'bg-gradient-to-r from-orange-500 to-amber-500'
                                : 'bg-gradient-to-r from-indigo-600 to-purple-600'
                        }`}>
                            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                                <ShoppingCart className="h-5 w-5" />
                                Confirm Order
                            </h3>
                            <p className="text-white/80 text-sm mt-0.5">
                                Please review before placing
                            </p>
                        </div>

                        {/* Content */}
                        <div className="p-5 space-y-4 max-h-[50vh] overflow-y-auto">
                            {/* Customer Info */}
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <User className="h-5 w-5 text-gray-400" />
                                <div>
                                    <p className="font-medium text-gray-900">{lead?.name || 'Customer'}</p>
                                    <p className="text-sm text-gray-500">{lead?.phone || 'No phone'}</p>
                                </div>
                            </div>

                            {/* Products Summary */}
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Products ({selectedProducts.length})</h4>
                                <div className="space-y-2 max-h-32 overflow-y-auto">
                                    {selectedProducts.map((sp, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-800 truncate">{sp.product.title}</p>
                                                <p className="text-xs text-gray-500">
                                                    {sp.variant.title !== 'Default Title' && sp.variant.title} × {sp.quantity}
                                                </p>
                                            </div>
                                            <span className="font-medium text-gray-900 ml-2">
                                                ₹{(parseFloat(sp.variant.price) * sp.quantity).toFixed(2)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Payment Method */}
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <span className="text-sm text-gray-600">Payment Method</span>
                                <span className={`text-sm font-medium px-2 py-1 rounded ${
                                    paymentType === 'razorpay_link'
                                        ? 'bg-green-100 text-green-700'
                                        : paymentType === 'cod'
                                        ? 'bg-orange-100 text-orange-700'
                                        : 'bg-indigo-100 text-indigo-700'
                                }`}>
                                    {paymentType === 'razorpay_link' ? 'Partial Payment' : paymentType === 'cod' ? 'Cash on Delivery' : 'Checkout URL'}
                                </span>
                            </div>

                            {/* Order Summary */}
                            <div className="border-t pt-3 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Subtotal</span>
                                    <span className="text-gray-900">₹{subtotal.toFixed(2)}</span>
                                </div>
                                {appliedDiscount && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-green-600">{appliedDiscount.title}</span>
                                        <span className="text-green-600">-₹{discountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {shippingAmount > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Shipping</span>
                                        <span className="text-gray-900">+₹{shippingAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {paymentType === 'razorpay_link' && razorpayAmount && parseFloat(razorpayAmount) > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Payment Amount</span>
                                        <span className="text-green-600 font-medium">₹{parseFloat(razorpayAmount).toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-base font-semibold pt-2 border-t">
                                    <span className="text-gray-900">Total</span>
                                    <span className="text-gray-900">₹{orderTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-5 py-4 bg-gray-50 border-t flex gap-3">
                            <button
                                onClick={handleCancelConfirmation}
                                className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmOrder}
                                className={`flex-1 px-4 py-2.5 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                                    paymentType === 'razorpay_link'
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : paymentType === 'cod'
                                        ? 'bg-orange-600 hover:bg-orange-700'
                                        : 'bg-indigo-600 hover:bg-indigo-700'
                                }`}
                            >
                                <CheckCircle className="h-4 w-4" />
                                Place Order
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};

export default FloatingOrderPanel;
